import React, { useEffect, useState , useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Wallet as WalletIcon, Check } from "lucide-react";

export default function Wallet() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [packages, setPackages] = useState({});
  const [boostPrices, setBoostPrices] = useState({});
  const [tx, setTx] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [meta, w] = await Promise.all([api.get("/meta/packages"), api.get("/wallet")]);
      setPackages(meta.data.credit_packages);
      setBoostPrices(meta.data.boost_prices);
      setTx(w.data.transactions);
    } catch (e) { console.warn("[Wallet] load error:", e); }
  }, []);

  const pollStatus = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 6) { toast("Status check timed out, refresh later"); return; }
    try {
      const { data } = await api.get(`/wallet/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success("Credits added to your wallet");
        await refresh();
        load();
        window.history.replaceState(null, "", "/wallet");
        return;
      }
      if (data.status === "expired") { toast.error("Session expired"); return; }
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch (e) {
      console.warn("[Wallet] poll error:", e);
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    }
  }, [refresh, load]);

  useEffect(() => {
    load();
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("session_id");
    if (sid) pollStatus(sid);
    if (url.searchParams.get("cancel")) toast.error("Payment cancelled");
  }, [load, pollStatus]);

  const buy = async (packageId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/checkout", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Could not start checkout"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="wallet-page">
      <p className="text-overline">{t.wallet.title}</p>
      <h1 className="font-heading text-4xl text-white mt-2 font-light">{t.wallet.balance}</h1>

      <div className="mt-6 card-luxe rounded-lg p-8 flex items-center justify-between bg-gradient-to-r from-[#1a1a1a] to-[#121212] border-gold/30">
        <div>
          <p className="text-overline">Current balance</p>
          <p className="font-heading text-6xl text-gold mt-2">{user?.credits ?? 0}</p>
          <p className="text-xs text-[#737373] mt-2">credits available</p>
        </div>
        <WalletIcon className="w-16 h-16 text-gold/30" strokeWidth={1} />
      </div>

      <h2 className="font-heading text-2xl text-white mt-12 mb-6">{t.wallet.buy}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {Object.entries(packages).map(([id, p], i) => (
          <div key={id} className={`rounded-lg p-7 ${i === 1 ? "bg-gradient-to-b from-[#1a1a1a] to-[#121212] border border-gold/50" : "card-luxe"}`} data-testid={`pkg-${id}`}>
            {i === 1 && <span className="inline-block px-2 py-1 rounded text-[10px] text-gold bg-gold/10 border border-gold/30 mb-3 tracking-widest">POPULAR</span>}
            <p className="text-overline">{p.label}</p>
            <p className="font-heading text-5xl text-white mt-2">{p.credits}<span className="text-base text-[#737373] ml-2">credits</span></p>
            <p className="text-2xl text-gold mt-3">${p.amount}</p>
            <ul className="mt-5 space-y-2 text-sm text-[#a3a3a3]">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Use for Boost / VIP / Top</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Credits never expire</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Instant top-up</li>
            </ul>
            <button onClick={() => buy(id)} disabled={busy} className={`mt-6 w-full rounded-md py-3 text-sm ${i === 1 ? "btn-gold" : "btn-ghost-dark"}`} data-testid={`buy-${id}`}>
              Buy {p.label}
            </button>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-2xl text-white mt-12 mb-6">Promotion prices</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {Object.entries(boostPrices).map(([id, b]) => (
          <div key={id} className="card-luxe rounded-lg p-5">
            <p className="text-overline">{b.label}</p>
            <p className="font-heading text-3xl text-gold mt-2">{b.credits} <span className="text-sm text-[#737373]">credits</span></p>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-2xl text-white mt-12 mb-6">{t.wallet.history}</h2>
      {tx.length === 0 ? (
        <p className="text-sm text-[#737373]">No transactions yet.</p>
      ) : (
        <div className="card-luxe rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a] text-[#737373]">
              <tr><th className="text-left p-3 font-normal">Date</th><th className="text-left p-3 font-normal">Package</th><th className="text-left p-3 font-normal">Credits</th><th className="text-left p-3 font-normal">Amount</th><th className="text-left p-3 font-normal">Status</th></tr>
            </thead>
            <tbody>
              {tx.map((t) => (
                <tr key={t.session_id} className="border-t border-white/5">
                  <td className="p-3 text-[#a3a3a3]">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="p-3 text-white">{t.package_id}</td>
                  <td className="p-3 text-gold">+{t.credits}</td>
                  <td className="p-3 text-white">${t.amount}</td>
                  <td className="p-3"><span className={`text-xs ${t.payment_status === "paid" ? "text-emerald-300" : "text-[#a3a3a3]"}`}>{t.payment_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
